import { Backdrop, Box } from '@mui/material';
import { EvergineCanvas } from 'evergine-react';
import { useCallback, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import './AppRouter.scss';
import { ForbiddenScreen, InfoMessage, NavBar, Spinner } from './common/components';
import { EVERGINE_CANVAS_ID } from './common/evergine/config';
import { useCaseId, useCaseType, useWindowSize } from './hooks';
import { useCalculateRoutes } from './hooks/shared/useCalculateRoutes';
import { PathLevels } from './models';
import { OrthodonticsPagesUrl, OrthodonticsRoutes } from './orthodontics/OrthodonticsRoutes';
import { useOrthBoundStore } from './orthodontics/stores/useStore';
import { Constants } from './shared';
import { SurgeryPagesUrl, SurgeryRoutes } from './surgeries/SurgeryRoutes';
import { Home } from './surgeries/components';
import {
  Modal,
  ModalAutoClosed,
  ModalClientValidationsCancelChanges,
  ModalClientValidationsEdit,
  ModalClientValidationsReject,
  ModalClientValidationsSendChanges,
  ModalClientValidationsValidate,
  ModalConfirmDeleteVersion,
  ModalDeleteAttach,
  ModalDownloadCase,
  ModalError,
  ModalImplant,
  ModalModificationsMade,
  ModalNextStep,
  ModalNextStepDirect,
  ModalNoMissingTeethInformation,
  ModalOnlyOneFileInformation,
  ModalPreviousCasePhaseModified,
  ModalPreviousStep,
  ModalPublishCase,
  ModalTypes
} from './surgeries/components/layout';
import { useBoundStore } from './surgeries/stores/useStore';

export enum BaseRoutes {
  Surgery = 'surgery',
  Orthodontics = 'orthodontics'
}

export enum CommonPagesUrl {
  GeneralInfo = '/general-information',
  SpecificInfo = '/specific-information',
  DicomCaptures = '/dicom-captures',
  StlCaptures = '/stl-captures',
  PhotoCaptures = '/photo-captures'
}

export const UrlsByCaseState: Record<string, string> = {
  stl_load: CommonPagesUrl.StlCaptures,
  teeth_segmentation: OrthodonticsPagesUrl.TeethSegmentation,
  axis_and_roots: OrthodonticsPagesUrl.AxisAndRoots,
  final_position: OrthodonticsPagesUrl.FinalPosition,
  intermediate_steps: OrthodonticsPagesUrl.IntermediateSteps,
  ipr: OrthodonticsPagesUrl.IPR,
  attaches: OrthodonticsPagesUrl.Attaches,
  publish: OrthodonticsPagesUrl.DentalMovements
};

export function AppRouter() {
  const { pathname } = useLocation();
  const [caseId] = useCaseId();
  const caseType = useCaseType();
  const [calculateNextRoute, calculatePreviousRoute] = useCalculateRoutes(pathname, caseId, caseType);
  const { evergineBusy, showCanvas, webBusy, messageInfo } = useBoundStore();
  const { setIsOrthContextMenuVisible, setPointerPosition } = useOrthBoundStore();
  const size = useWindowSize();
  const [t] = useTranslation();

  const currentRoute = useMemo(() => `/${pathname.split('/')[PathLevels.RouteView]}`, [pathname]);

  const allowedPagesToShowCanvas: string[] = [
    CommonPagesUrl.DicomCaptures,
    CommonPagesUrl.StlCaptures,
    SurgeryPagesUrl.Matching,
    SurgeryPagesUrl.ResultMatching,
    SurgeryPagesUrl.Implants,
    OrthodonticsPagesUrl.MeshCorrection,
    OrthodonticsPagesUrl.DentalMovements,
    OrthodonticsPagesUrl.TeethSegmentation,
    OrthodonticsPagesUrl.IntermediateSteps,
    OrthodonticsPagesUrl.AxisAndRoots,
    OrthodonticsPagesUrl.FinalPosition,
    OrthodonticsPagesUrl.IPR,
    OrthodonticsPagesUrl.Attaches,
    OrthodonticsPagesUrl.Publish
  ];

  const pagesWithoutOffset: string[] = [OrthodonticsPagesUrl.TeethSegmentation, OrthodonticsPagesUrl.AxisAndRoots];

  const pagesWithInfoMessage: Record<string, string>[] = [
    { page: OrthodonticsPagesUrl.TeethSegmentation },
    { page: OrthodonticsPagesUrl.FinalPosition }
  ];

  const pagesInfoMessage = [
    OrthodonticsPagesUrl.TeethSegmentation,
    OrthodonticsPagesUrl.AxisAndRoots,
    OrthodonticsPagesUrl.FinalPosition,
    OrthodonticsPagesUrl.Attaches
  ];

  const handleContextMenu = useCallback((e: any) => {
    const position = { clientX: e.clientX, clientY: e.clientY };
    setPointerPosition(position);
    setIsOrthContextMenuVisible(true); // TODO: reactivate this when the ortho context menu is implemented
  }, []);

  const calculateHeight = useCallback(() => {
    const applyOffset = !pagesWithoutOffset.includes(currentRoute);
    const offset = applyOffset ? Constants.canvasHeightOffset : 0;
    return size.height - offset;
  }, [pagesWithoutOffset, pathname, size, currentRoute]);

  const showInfoMessageIfNeeded = useCallback(() => {
    const isPageWithInfoMessage = pagesInfoMessage.map(String).includes(currentRoute);
    return isPageWithInfoMessage && <InfoMessage message={t(messageInfo)} />;
  }, [currentRoute, pagesWithInfoMessage, messageInfo]);

  return (
    <>
      <ErrorBoundary
        FallbackComponent={(): JSX.Element => <div className="error-boundary">{t('errors.boundaryError')}</div>}
      >
        <NavBar />
      </ErrorBoundary>
      <ErrorBoundary
        FallbackComponent={(): JSX.Element => <div className="error-boundary">{t('errors.boundaryError')}</div>}
      >
        <Modal>
          <ModalNextStep key={ModalTypes.ModalNextStep} nextRoute={calculateNextRoute()} />
          <ModalNextStepDirect key={ModalTypes.ModalNextStepDirect} nextRoute={calculateNextRoute()} />
          <ModalPreviousStep key={ModalTypes.ModalPreviousStep} previousRoute={calculatePreviousRoute()} />
          <ModalError key={ModalTypes.ModalError} />
          <ModalImplant key={ModalTypes.ModalImplant} />
          <ModalClientValidationsValidate key={ModalTypes.ModalClientValidationsValidate} />
          <ModalClientValidationsEdit key={ModalTypes.ModalClientValidationsEdit} />
          <ModalClientValidationsReject key={ModalTypes.ModalClientValidationsReject} />
          <ModalConfirmDeleteVersion key={ModalTypes.ModalConfirmDeleteVersion} />
          <ModalClientValidationsSendChanges key={ModalTypes.ModalClientValidationsSendChanges} />
          <ModalAutoClosed key={ModalTypes.ModalAutoClosed} />
          <ModalClientValidationsCancelChanges key={ModalTypes.ModalClientValidationsCancelChanges} />
          <ModalOnlyOneFileInformation key={ModalTypes.ModalOnlyOneFileInformation} nextRoute={calculateNextRoute()} />
          <ModalPreviousCasePhaseModified key={ModalTypes.ModalPreviousCasePhaseModified} />
          <ModalNoMissingTeethInformation key={ModalTypes.ModalNoMissingTeethInformation} />
          <ModalNoMissingTeethInformation key={ModalTypes.ModalNoMissingTeethInformation} />
          <ModalPublishCase key={ModalTypes.ModalPublishCase} />
          <ModalDownloadCase key={ModalTypes.ModalDownloadCase} />
          <ModalDeleteAttach key={ModalTypes.ModalDeleteAttach} />
          <ModalModificationsMade key={ModalTypes.ModalModificationsMade} />
        </Modal>
        <Routes>
          <Route path="/forbidden" element={<ForbiddenScreen />} />
          <Route path="/" element={<Home />} />
          {SurgeryRoutes()}
          {OrthodonticsRoutes()}

          <Route path="*" element={<Navigate replace to="/" />} />
        </Routes>
      </ErrorBoundary>

      <div className="section-content d-flex align-items-center justify-content-center">
        <Backdrop style={{ zIndex: 1000 }} open={evergineBusy || webBusy}>
          <Box sx={{ display: 'block', textAlign: 'center' }}>
            <Spinner />
          </Box>
        </Backdrop>

        <div
          className="app"
          style={{
            display:
              showCanvas && allowedPagesToShowCanvas.includes(`/${pathname.split('/')[PathLevels.RouteView]}`)
                ? 'inline'
                : 'none'
          }}
        >
          <ErrorBoundary
            FallbackComponent={(): JSX.Element => <div className="error-boundary">{t('errors.boundaryError')}</div>}
          >
            <EvergineCanvas
              canvasId={EVERGINE_CANVAS_ID}
              width={size.width}
              height={calculateHeight()}
              onContextMenu={(e: any) => handleContextMenu(e)}
            />
          </ErrorBoundary>
        </div>
      </div>
      {showInfoMessageIfNeeded()}
    </>
  );
}
